import React, { useState } from 'react';
import {
  array,
  arrayOf,
  bool,
  func,
  shape,
  string,
  oneOf,
  object,
  number,
} from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { FormattedMessage, intlShape, useIntl } from '../../util/reactIntl';
import {
  LISTING_STATE_PENDING_APPROVAL,
  LISTING_STATE_CLOSED,
  SCHEMA_TYPE_MULTI_ENUM,
  SCHEMA_TYPE_TEXT,
  propTypes,
} from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
  parse,
} from '../../util/urlHelpers';
import { convertMoneyToNumber } from '../../util/currency';
import {
  calculateDistance,
  ensureListing,
  ensureOwnListing,
  ensureUser,
  normalizeDeliveryOptions,
  sanitizeHtml,
  stripHtmlTags,
  userDisplayNameAsString,
} from '../../util/data';
import { richText } from '../../util/richText';
import {
  isBookingProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';

import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  manageDisableScrolling,
  isScrollingDisabled,
} from '../../ducks/ui.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';
import { withViewport } from '../../util/uiHelpers';

import {
  H4,
  Page,
  NamedLink,
  NamedRedirect,
  OrderPanel,
  LayoutSingleColumn,
  Button,
  Modal,
  SecondaryButton,
  Form,
  FieldTextInput,
  AboutOwner,
  IconCollection,
  SuggestedUses,
  WhereWorksBest,

  ReviewSummary,
  StoreReviews,
  EnvironmentalImpact
} from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';
import InstructionsToUse from './InstructionToUse/InstructionToUse.js';

import {
  sendInquiry,
  setInitialValues,
  fetchTimeSlots,
  fetchTransactionLineItems,
  replicateListingThunk,
} from './ListingPage.duck';

import {
  LoadingPage,
  ErrorPage,
  priceData,
  listingImages,
  handleContactUser,
  handleSubmitInquiry,
  handleSubmit,
} from './ListingPage.shared';
import ActionBarMaybe from './ActionBarMaybe';
import SectionMapMaybe from './SectionMapMaybe';

import AuthorListings from './AuthorListings/AuthorListings';
import { addToCart } from '../../ducks/cart.duck';
import classNames from 'classnames';
import {
  createResourceLocatorString,
  pathByRouteName,
} from '../../util/routes';

import { Form as FinalForm } from 'react-final-form';

import css from './ListingPage.module.css';
import {
  composeValidators,
  stringValidator,
  maxLength,
  required,
} from '../../util/validators.js';
import { isPermitted } from '../../util/currentUser.js';
import { feeTypeMapper, USAGE_MULTIPLE } from '../../util/constants.js';
import { getLocationText } from '../../util/data.js';
import Container from '../../components/Layout/Container.js';
import Grid from '../../components/Layout/Grid.js';
import Column from '../../components/Layout/Column.js';
import DescriptionRentalTermsTabs from '../ListingWizard/ListingWizardPreview/DescriptionRentalTermsTabs.js';
import BookingSupport from '../../components/BookingSupport/BookingSupport.js';
import BlogListing from '../../components/BlogListing/BlogListing.js';
import ListingTopbar from './ListingTopbar.js';
import SectionGallery from './SectionGallery.js';
import ListingPageHeader from './ListingPageHeader.js';
import InquiryForm from './InquiryForm/InquiryForm.js';

const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;
const TITLE_MAX_LENGTH = 60;

const { UUID, Money } = sdkTypes;

const createFilterOptions = options =>
  options.map(o => ({ key: `${o.option}`, label: o.label }));

const Notification = props => {
  const { notification, addToCartError } = props;
  const notificationClasses = classNames(
    css.notification,
    addToCartError ? css.error : css.success
  );

  return notification ? (
    <div className={notificationClasses}>{notification}</div>
  ) : null;
};

const CopyListingModal = props => {
  const [showFields, setShowFields] = useState(false);

  const {
    show,
    onClose,
    onManageDisableScrolling,
    currentListingTitle,
    listingId,
    currentListingDescription,
    intl,
    onReplicateListing,
    history,
    routes,
    disableCopyListing,
    replicateListingError,
    replicateListingInProgress,
  } = props;

  const formIntials = {
    title: currentListingTitle,
    description: currentListingDescription,
  };
  const replicateListingErrorMsg = intl.formatMessage({
    id: 'ListingPage.CopyListingModal.replicateListingError',
  });

  // title validators
  const maxLengthMessage = intl.formatMessage(
    { id: 'ListingPage.CopyListingModal.maxLength' },
    {
      maxLength: TITLE_MAX_LENGTH,
    }
  );
  const titleRequiredMessage = intl.formatMessage({
    id: 'ListingPage.CopyListingModal.titleRequired',
  });
  const mustChangeTitleMessage = intl.formatMessage(
    { id: 'ListingPage.CopyListingModal.mustChangeTitle' },
    { words: 'one' }
  );

  const maxLength60Message = maxLength(maxLengthMessage, TITLE_MAX_LENGTH);
  const titleValueValidator = stringValidator({
    message: mustChangeTitleMessage,
    currentStr: currentListingTitle,
    shouldDifferentBy: 1,
  });
  const titleValidators = composeValidators(
    required(titleRequiredMessage),
    maxLength60Message,
    titleValueValidator
  );

  // Description validators
  const descriptionRequiredMsg = intl.formatMessage({
    id: 'ListingPage.ReplicateModal.descriptionRequired',
  });
  const mustChangeDescriptionMsg = intl.formatMessage(
    { id: 'ListingPage.CopyListingModal.mustChangeDescription' },
    { words: 'ten' }
  );
  const descriptionRequired = required(descriptionRequiredMsg);
  const descriptionValueValidator = stringValidator({
    message: mustChangeDescriptionMsg,
    currentStr: currentListingDescription,
    shouldDifferentBy: 10,
  });

  const descriptionValidators = composeValidators(
    descriptionRequired,
    descriptionValueValidator
  );

  const handleReplicateListing = values => {
    const { title, description } = values;
    onReplicateListing({ listingId, title, description }).then(res => {
      const { listingId, type } = res;
      history.push(
        createResourceLocatorString('EditListingPage', routes, {
          slug: 'draft',
          id: listingId,
          type,
          tab: 'details',
        })
      );
    });
  };

  if (disableCopyListing) {
    return null;
  }

  return (
    <Modal
      className={css.copyListingModal}
      isOpen={show}
      onClose={onClose}
      onManageDisableScrolling={onManageDisableScrolling}
    >
      {showFields ? (
        <FinalForm
          initialValues={formIntials}
          onSubmit={handleReplicateListing}
          render={formRenderProps => {
            const {
              handleSubmit,
              formId,
              invalid,
              submitInProgress,
            } = formRenderProps;

            const submitDisabled = invalid || submitInProgress;

            return (
              <Form className={css.replicateModalFrom} onSubmit={handleSubmit}>
                <span className={css.whyChange}>
                  <FormattedMessage id="ListingPage.CopyListingModal.whyChange" />
                </span>
                {replicateListingError ? (
                  <span className={css.replicateError}>
                    {replicateListingErrorMsg}
                  </span>
                ) : null}
                <FieldTextInput
                  id={`${formId}title`}
                  name="title"
                  className={css.title}
                  type="text"
                  label={intl.formatMessage({
                    id: 'ListingPage.ReplicateModal.title',
                  })}
                  placeholder={intl.formatMessage({
                    id: 'ListingPage.ReplicateModal.titlePlaceholder',
                  })}
                  maxLength={TITLE_MAX_LENGTH}
                  validate={titleValidators}
                />

                <FieldTextInput
                  id={`${formId}description`}
                  name="description"
                  className={css.description}
                  type="textarea"
                  label={intl.formatMessage({
                    id: 'ListingPage.ReplicateModal.description',
                  })}
                  placeholder={intl.formatMessage({
                    id: 'ListingPage.ReplicateModal.descriptionPlaceholder',
                  })}
                  validate={descriptionValidators}
                />
                <Button
                  className={css.submitButton}
                  type="submit"
                  inProgress={replicateListingInProgress}
                  disabled={submitDisabled}
                >
                  Replicate
                </Button>
              </Form>
            );
          }}
        />
      ) : (
        <div>
          <span>
            <FormattedMessage id="ListingPage.replicateListing" />
          </span>
          <div className={css.actionBtns}>
            <Button
              className={css.replicateBtn}
              onClick={() => setShowFields(true)}
            >
              <FormattedMessage id="ListingPage.replicateButton" />
            </Button>
            <SecondaryButton onClick={onClose}>
              <FormattedMessage id="ListingPage.replicateLaterButton" />
            </SecondaryButton>
          </div>
        </div>
      )}
    </Modal>
  );
};

[
  {
    key: 'checkoutOptions',
    scope: 'public',
    schemaType: 'multi-enum',
    includeForListingTypes: ['general'],
    enumOptions: [
      {
        label: 'Charged once per cart only',
        value: 'oncePerCart',
        disabled: false,
      },
      {
        label: 'Charged once per listing',
        value: 'oncePerListing',
        disabled: false,
      },
      { label: 'Charged once per item', value: 'oncePerItem', disabled: false },
    ],
  },
];

const ShowAdditionalFees = ({ listing }) => {
  const { publicData } = listing.attributes;
  const { additionalFees = [] } = publicData;
  // const otherOptionalCharges = additionalFees?.filter(fee => (fee.feeCalculationType !== USAGE_MULTIPLE) && (fee.whenToApply === REQUIRED_UPFRONT_OPTIONAL));

  if (!additionalFees.length) {
    return null;
  }

  return (
    <div className={css.additionalFeesList}>
      <h4 className={css.additionalFeesSection}>
        <FormattedMessage id="ListingPage.additionalFees" />
      </h4>
      {additionalFees.map((fee, index) => (
        <div key={index} className={css.additionalFees}>
          <span className={css.feeType}>{feeTypeMapper[fee.feeType]}</span>
        </div>
      ))}
    </div>
  );
};

const getLongestRentalPeriod = prices => {
  if (!prices || typeof prices !== 'object') {
    throw new Error('Prices are missing or invalid in listing data');
  }

  // Priority order for the longest rental period
  const rentalPeriods = [
    'twentyEightDaysPrice',
    'oneWeekPrice',
    'twoDaysPrice',
    'perDayPrice',
    'perHourPrice',
    'perMinutePrice',
  ];

  // Find the longest rental period available in the prices object
  let longestPeriodKey = null;
  for (const period of rentalPeriods) {
    if (prices[period]) {
      longestPeriodKey = period;
      break;
    }
  }

  if (!longestPeriodKey) {
    throw new Error('No valid rental periods found in prices');
  }

  return longestPeriodKey;
};

const getDefaultDailyPrice = listing => {
  if (!listing || !listing.attributes || !listing.attributes.publicData) {
    throw new Error('Invalid listing data');
  }

  const publicData = listing.attributes.publicData;
  const prices = publicData.prices;

  if (!prices || typeof prices !== 'object') {
    throw new Error('Prices are missing or invalid in listing data');
  }

  // Find the longest rental period available in the prices object
  let longestPeriodKey = getLongestRentalPeriod(prices);

  if (!longestPeriodKey) {
    throw new Error('No valid rental periods found in prices');
  }

  const longestPeriodPrice = prices[longestPeriodKey];

  if (
    !longestPeriodPrice ||
    !longestPeriodPrice.amount ||
    !longestPeriodPrice.currency
  ) {
    throw new Error('Invalid price data for the longest rental period');
  }

  let dailyPrice;

  // Calculate the daily price based on the rental period
  switch (longestPeriodKey) {
    case 'twentyEightDaysPrice':
      dailyPrice = longestPeriodPrice.amount / 28;
      break;
    case 'oneWeekPrice':
      dailyPrice = longestPeriodPrice.amount / 7;
      break;
    case 'twoDaysPrice':
      dailyPrice = longestPeriodPrice.amount / 2;
      break;
    case 'perDayPrice':
      dailyPrice = longestPeriodPrice.amount;
      break;
    case 'perHourPrice':
      dailyPrice = longestPeriodPrice.amount;
      break;
    case 'perMinutePrice':
      dailyPrice = longestPeriodPrice.amount * 60;
      break;
    default:
      throw new Error('Unhandled rental period');
  }

  return new Money(dailyPrice, longestPeriodPrice.currency);
};

const getDefaultUnit = listing => {
  const pricingKeys = listing?.attributes?.publicData?.prices || {};

  // Find the longest rental period available in the prices object
  let longestPeriodKey = getLongestRentalPeriod(pricingKeys);

  // Define mappings for daily and hourly units
  const dailyKeys = [
    'perDayPrice',
    'twoDaysPrice',
    'oneWeekPrice',
    'twentyEightDaysPrice',
    'overnightPrice',
  ];
  const hourlyKeys = ['perHourPrice', 'perMinutePrice'];

  // Helper function to determine the unit
  const determineUnit = key => {
    if (dailyKeys.includes(key)) {
      return 'day';
    } else if (hourlyKeys.includes(key)) {
      return 'hour';
    }
    return null;
  };

  if (longestPeriodKey && pricingKeys[longestPeriodKey]) {
    return determineUnit(longestPeriodKey);
  }

  const availableKeys = Object.keys(pricingKeys);
  if (availableKeys.length > 0) {
    return determineUnit(availableKeys[0]);
  }

  return null;
};

export const ListingPageComponent = props => {
  const [inquiryModalOpen, setInquiryModalOpen] = useState(
    props.inquiryModalOpenForListingId === props.params.id
  );

  const {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    intl,
    onManageDisableScrolling,
    params: rawParams,
    location,
    scrollingDisabled,
    showListingError,
    reviews,
    fetchReviewsError,
    sendInquiryInProgress,
    sendInquiryError,
    monthlyTimeSlots,
    onFetchTimeSlots,
    onFetchTransactionLineItems,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    history,
    callSetInitialValues,
    onSendInquiry,
    onInitializeCardPaymentData,
    config,
    routeConfiguration,
    selectedCurrency,
    exchangeRates,
    fetchRatesError,
    fetchRatesInProgress,
    authorListings,
    authorListingsInProgress,
    authorListingsError,
    onAddToCart,
    addToCartInProgress,
    addToCartError,
    cartNotification,
    onReplicateListing,
    replicateListingError,
    replicateListingInProgress,
    isVerified,
    totalListings,
    blogs,
    fetchBlogsInProgress,
    fetchBlogsError,
    viewport,
    currentUserLocationInfo,
  } = props;
  const { allowCopy } = parse(location.search);
  const [isCopyListingPopup, setIsCopyListingPopup] = useState(allowCopy);

  // Extract the search part of the URL
  const searchParams = new URLSearchParams(location.search);

  // Get the value of the 'search' parameter (which is another encoded query string)
  const nestedQueryString = searchParams.get('search');

  // Decode the nested query string
  const decodedNestedQueryString = decodeURIComponent(nestedQueryString);

  // Create a new URLSearchParams object for the nested query string
  const nestedParams = new URLSearchParams(decodedNestedQueryString);

  // Get the value of the 'queryID' parameter
  const queryID = nestedParams.get('queryID');

  const rawSlug = rawParams?.slug;
  const listingConfig = config.listing;
  const listingId = new UUID(rawParams.id);
  const isPendingApprovalVariant =
    rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;
  const currentListing =
    isPendingApprovalVariant || isDraftVariant
      ? ensureOwnListing(getOwnListing(listingId))
      : ensureListing(getListing(listingId));

  const listingSlug =
    rawParams.slug || createSlug(currentListing.attributes.title || '');
  const params = { slug: listingSlug, ...rawParams };

  const listingPathParamType = isDraftVariant
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;
  const listingTab = isDraftVariant ? 'photos' : 'details';

  const isApproved =
    currentListing.id &&
    currentListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

  const pendingIsApproved = isPendingApprovalVariant && isApproved;

  // If a /pending-approval URL is shared, the UI requires
  // authentication and attempts to fetch the listing from own
  // listings. This will fail with 403 Forbidden if the author is
  // another user. We use this information to try to fetch the
  // public listing.
  const pendingOtherUsersListing =
    (isPendingApprovalVariant || isDraftVariant) &&
    showListingError &&
    showListingError.status === 403;
  const shouldShowPublicListingPage =
    pendingIsApproved || pendingOtherUsersListing;

  if (shouldShowPublicListingPage) {
    return (
      <NamedRedirect
        name="ListingPage"
        params={params}
        search={location.search}
      />
    );
  }

  if (!rawSlug && listingSlug && listingSlug !== 'no-slug') {
    return (
      <NamedRedirect
        name="ListingPage"
        params={params}
        search={location.search}
      />
    );
  }

  const topbar = <><div className={css.topbarMobile}><ListingTopbar history={history} /></div><div className={css.topbarDesktop}><TopbarContainer /></div></>;

  if (showListingError && showListingError.status === 404) {
    // 404 listing not found
    return <NotFoundPage />;
  } else if (showListingError) {
    // Other error in fetching listing
    return (
      <ErrorPage
        topbar={topbar}
        scrollingDisabled={scrollingDisabled}
        intl={intl}
      />
    );
  } else if (!currentListing.id) {
    // Still loading the listing
    return (
      <LoadingPage
        topbar={topbar}
        scrollingDisabled={scrollingDisabled}
        intl={intl}
      />
    );
  }

  const {
    description = '',
    geolocation = null,
    price = null,
    title = '',
    publicData = {},
    metadata = {},
  } = currentListing.attributes;

  const { city,
    postCode,
    region,
    country,
    instructions,
    quantity,
    suggested_usage_of_this_item,
    where_the_item_can_be_used,
    online_review_summary,
    environmental_impact,
    faqs,
    enabledOptionalFields = [], } = publicData || {};
  const showInstructions = !!instructions && enabledOptionalFields.includes('instructions');
  const showSuggestedUsage = !!suggested_usage_of_this_item && enabledOptionalFields.includes('suggested_usage_of_this_item');
  const showWhereTheItemCanBeUsed = !!where_the_item_can_be_used && enabledOptionalFields.includes('where_the_item_can_be_used');
  const showOnlineReviewSummary = !!online_review_summary && enabledOptionalFields.includes('online_review_summary');
  const showFaqs = !!faqs && enabledOptionalFields.includes('faqs');

  const listingDistance = calculateDistance(currentUserLocationInfo?.origin?.lat,
    currentUserLocationInfo?.origin?.lng,
    geolocation?.lat,
    geolocation?.lng);
  const showInMiles = currentUserLocationInfo?.country === 'US';
  const defaultPrice = getDefaultDailyPrice(currentListing);
  const locationText = getLocationText(city, postCode, region, title);
  const defaultUnit = getDefaultUnit(currentListing);
  const richedTitle = richText(title, {
    longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
    longWordClass: css.longWord,
  });

  const richTitle = (
    <span>
      {!!city ? (
        <FormattedMessage
          id="ListingPage.titleWithLocation"
          values={{ title: richedTitle, city }}
        />
      ) : (
        richedTitle
      )}
    </span>
  );

  const authorAvailable = currentListing && currentListing.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
  const isOwnListing =
    userAndListingAuthorAvailable &&
    currentListing.author.id.uuid === currentUser.id.uuid;

  const transactionProcessAlias = publicData?.transactionProcessAlias;
  const processName = resolveLatestProcessName(
    transactionProcessAlias?.split('/')?.[0]
  );
  const isBooking = isBookingProcess(processName);
  const isPurchase = isPurchaseProcess(processName);
  const processType = isBooking
    ? 'booking'
      ? isPurchase
      : 'purchase'
    : 'inquiry';

  const currentAuthor = authorAvailable ? currentListing.author : null;
  const ensuredAuthor = ensureUser(currentAuthor);
  const noPayoutDetailsSetWithOwnListing =
    isOwnListing &&
    (processType !== 'inquiry' && !currentUser?.attributes?.stripeConnected);
  const payoutDetailsWarning = noPayoutDetailsSetWithOwnListing ? (
    <span className={css.payoutDetailsWarning}>
      <FormattedMessage
        id="ListingPage.payoutDetailsWarning"
        values={{ processType }}
      />
      <NamedLink name="StripePayoutPage">
        <FormattedMessage id="ListingPage.payoutDetailsWarningLink" />
      </NamedLink>
    </span>
  ) : null;

  // When user is banned or deleted the listing is also deleted.
  // Because listing can be never showed with banned or deleted user we don't have to provide
  // banned or deleted display names for the function
  const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');
  const { storeName, rating = 5 } =
    ensuredAuthor.attributes.profile?.publicData || {};
  const { formattedPrice: defaultFormattedPrice } = priceData(
    defaultPrice,
    defaultPrice.currency,
    intl
  );
  const commonParams = { params, history, routes: routeConfiguration };
  const onContactUser = handleContactUser({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    location,
    setInitialValues,
    setInquiryModalOpen,
  });
  // Note: this is for inquiry state in booking and purchase processes. Inquiry process is handled through handleSubmit.
  const onSubmitInquiry = handleSubmitInquiry({
    ...commonParams,
    getListing,
    onSendInquiry,
    setInquiryModalOpen,
  });

  const usageMultipleCharges =
    publicData?.additionalFees?.filter(
      fee => fee.feeCalculationType === USAGE_MULTIPLE
    ) || [];

  const onSubmit = handleSubmit({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    getListing,
    onInitializeCardPaymentData,
    lineItems,
    usageMultipleCharges,
  });

  const isCurrentlyClosed =
    currentListing.attributes.state === LISTING_STATE_CLOSED;

  const handleOrderSubmit = values => {
    if (isOwnListing || isCurrentlyClosed) {
      window.scrollTo(0, 0);
    } else {
      onSubmit(values);
    }
  };
  const facebookImages = listingImages(currentListing, 'facebook');
  const twitterImages = listingImages(currentListing, 'twitter');
  const schemaImages = listingImages(
    currentListing,
    `${config.layout.listingImage.variantPrefix}-2x`
  ).map(img => img.url);
  const marketplaceName = config.marketplaceName;
  const schemaTitle = !!city ? intl.formatMessage(
    { id: 'ListingPage.schemaTitlePrimary' },
    { title, city, price: defaultFormattedPrice, unit: defaultUnit, marketplaceName }
  ) : intl.formatMessage(
    { id: 'ListingPage.schemaTitleSecondary' },
    { title, price: defaultFormattedPrice, unit: defaultUnit, marketplaceName }
  );

  const plainDescription = stripHtmlTags(description);
  const seoOptimisedDescription = !!city ? intl.formatMessage(
    { id: 'ListingPage.seoOptimisedDescriptionPrimary' },
    { title, city, price: defaultFormattedPrice, unit: defaultUnit, description: plainDescription }
  ) : intl.formatMessage(
    { id: 'ListingPage.seoOptimisedDescriptionSecondary' },
    { title, price: defaultFormattedPrice, unit: defaultUnit, description: plainDescription }
  );


  // You could add reviews, sku, etc. into page schema
  // Read more about product schema
  // https://developers.google.com/search/docs/advanced/structured-data/product
  const productURL = `${config.marketplaceRootURL}${location.pathname}${location.search}${location.hash}`;
  const schemaPriceMaybe = defaultPrice
    ? {
      price: intl.formatNumber(convertMoneyToNumber(defaultPrice), {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
      priceCurrency: defaultPrice.currency,
    }
    : {};
  const currentStock = currentListing.currentStock?.attributes?.quantity || 0;
  const schemaAvailability = 'https://schema.org/InStock';
  // currentStock > 0
  //   ? 'https://schema.org/InStock'
  //   : 'https://schema.org/OutOfStock';

  const addToCartHandler = params => {
    // Check if item is of current user
    if (isOwnListing || isCurrentlyClosed) {
      window.scrollTo(0, 0);
    } else {
      onAddToCart(config, params, queryID)
        .then(r => {
          const cartPagePath = pathByRouteName('CartPage', routeConfiguration);
          history.push(cartPagePath);
        })
        .catch(() => {
          // do nothing redux will manage.
        });
    }
  };

  const isCurrentUser =
    currentListing.author?.id?.uuid === currentUser?.id?.uuid;
  const disableCopyListing = !isPermitted(currentUser) || isCurrentUser;


  const isMobile = viewport?.width < 1024;

  const formatCancelation = (cancelationPolicy) => {
    if (!cancelationPolicy || !cancelationPolicy?.length) return null;
    if (!cancelationPolicy?.includes('Cancelation')) {
      return `${cancelationPolicy} Cancelation`
    }
    return cancelationPolicy
  };
  const formatAccessibility = (accessibility) => {
    if (!accessibility || !accessibility?.length) return null;
    return accessibility?.map((item) => {
      if (!item.includes('available')) {
        return `${item} available`
      }
      return item;
    })
  };

  const formatCondition = (condition) => {
    if (!condition || !condition?.length) return null;
    if (condition.includes('its_a_service')) {
      return null;
    }
    if (!condition.includes('Condition')) {
      return `${condition} Condition`
    }
    return condition;
  };

  const formatReasonForListing = (reasonForListing) => {
    if (!reasonForListing || !reasonForListing?.length) return null;
    return reasonForListing.map((item) => {
      if (item.includes('just_because')) {
        return null
      }
      return item;
    })
  };

  const { access_to_rentals, reason_for_listing, Condition_of_the_item, cancelation_policy } = publicData;
  const formattedCondition = formatCondition(Condition_of_the_item);
  const formattedCancelation = formatCancelation(cancelation_policy);
  const formattedAccessibility = formatAccessibility(access_to_rentals);
  const formattedReasonForListing = formatReasonForListing(reason_for_listing);

  const pills = [
    formattedCondition,
    formattedCancelation,
    ...(formattedAccessibility?.length > 0 ? normalizeDeliveryOptions(formattedAccessibility) : []),
    ...(formattedReasonForListing?.length > 0 ? formattedReasonForListing : []),
  ].filter(Boolean);

  const showEnvironmentalImpactSection = environmental_impact &&  publicData?.reason_for_listing?.includes('help_the_environment');
  const environmentalImpactData = Array.isArray(environmental_impact)
    ? { bullets: environmental_impact } 
   : environmental_impact?.benefits && environmental_impact?.paragraph
    ? { bullets: environmental_impact.benefits, paragraph: environmental_impact.paragraph }
    : null;

  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      author={storeName || authorDisplayName}
      description={seoOptimisedDescription}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'Product',
        description: seoOptimisedDescription,
        name: schemaTitle,
        image: schemaImages,
        offers: {
          '@type': 'Offer',
          url: productURL,
          ...schemaPriceMaybe,
          availability: schemaAvailability,
        },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: rating || 5,
          ratingCount: reviews.length || 1,
        },
      }}
    >
      <Modal
        id="ListingPage.inquiry"
        contentClassName={css.inquiryModalContent}
        isOpen={inquiryModalOpen}
        onClose={() => setInquiryModalOpen(false)}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <InquiryForm
          className={css.inquiryForm}
          submitButtonWrapperClassName={css.inquirySubmitButtonWrapper}
          listingTitle={title}
          authorDisplayName={authorDisplayName}
          sendInquiryError={sendInquiryError}
          onSubmit={onSubmitInquiry}
          inProgress={sendInquiryInProgress}
          isVerified={isVerified}
          rating={rating || 5}
        />
      </Modal>
      <LayoutSingleColumn
        className={css.pageRoot}
        topbar={topbar}
        footer={<FooterContainer />}
      >
        <Container className={css.mainContainer}>
          <Grid>
            <Column>

              <Grid columns={12} className={css.grid}>
                <Column lg={8} md={8} sm={12} className={css.mainGrid}>
                  <CopyListingModal
                    disableCopyListing={disableCopyListing}
                    show={isCopyListingPopup}
                    onClose={() => setIsCopyListingPopup(false)}
                    onManageDisableScrolling={onManageDisableScrolling}
                    onReplicateListing={onReplicateListing}
                    currentListingTitle={title}
                    currentListingDescription={description}
                    listingId={rawParams.id}
                    intl={intl}
                    history={history}
                    routes={routeConfiguration}
                    replicateListingInProgress={replicateListingInProgress}
                    replicateListingError={replicateListingError}
                  />

                  {/* Breadcrumb Navigation (Optional but SEO-friendly) */}
                  <nav aria-label="Breadcrumb" className={css.breadcrumb}>
                    <NamedLink className={css.separator} style={{ marginLeft: 0 }} name="LandingPage">
                      <span>Home</span>
                    </NamedLink>
                    <IconCollection name="arrowSeparator" />
                    <p aria-current="page" className={css.separator}>
                      {title}
                    </p>
                  </nav>

                  {/* Listing Page Header */}
                  <ListingPageHeader
                    className={css.pillsContainerDesktop}
                    pills={pills}
                    title={title}
                    rating={rating}
                    reviews={reviews}
                    city={city}
                    region={region}
                    country={country}
                    listingDistance={listingDistance}
                    showInMiles={showInMiles}
                    richTitle={richTitle}
                  />
                  {/* <div className={css.mainColumnForProductLayout}> */}
                  {currentListing.id && noPayoutDetailsSetWithOwnListing ? (
                    <ActionBarMaybe
                      className={css.actionBarForProductLayout}
                      isOwnListing={isOwnListing}
                      listing={currentListing}
                      showNoPayoutDetailsSet={noPayoutDetailsSetWithOwnListing}
                    />
                  ) : null}
                  {currentListing.id ? (
                    <ActionBarMaybe
                      className={css.actionBarForProductLayout}
                      isOwnListing={isOwnListing}
                      listing={currentListing}
                      editParams={{
                        id: listingId.uuid,
                        slug: listingSlug,
                        type: listingPathParamType,
                        tab: listingTab,
                      }}
                    />
                  ) : null}
                  <SectionGallery
                    listing={currentListing}
                    variantPrefix={config.layout.listingImage.variantPrefix}
                  />

                  <ListingPageHeader
                    className={css.pillsContainerMobile}
                    pills={pills}
                    title={title}
                    rating={rating}
                    reviews={reviews}
                    city={city}
                    region={region}
                    country={country}
                    richTitle={richTitle}
                  />
                  {isMobile ? (
                    <Column lg={4} md={4} sm={12} className={css.mobileColumn}>
                      <div className={css.orderColumnForProductLayout}>
                        <OrderPanel
                          rootClassName={css.productOrderPanel}
                          listing={currentListing}
                          isOwnListing={isOwnListing}
                          onSubmit={handleOrderSubmit}
                          showAuthorDetails
                          breakdownTitle={<FormattedMessage id="BookingDatesForm.priceBreakdownTitle" />}
                          authorLink={
                            <NamedLink
                              className={css.authorNameLink}
                              name="ListingPage"
                              params={params}
                              to={{ hash: '#author' }}
                            >
                              {storeName || authorDisplayName}
                            </NamedLink>
                          }
                          title={
                            <FormattedMessage
                              id="ListingPage.orderTitle"
                              values={{ title: richTitle }}
                            />
                          }
                          titleDesktop={
                            <H4 as="h1" className={css.orderPanelTitle}>
                              <FormattedMessage
                                id="ListingPage.orderTitle"
                                values={{ title: richTitle }}
                              />
                            </H4>
                          }
                          payoutDetailsWarning={payoutDetailsWarning}
                          author={ensuredAuthor}
                          onManageDisableScrolling={onManageDisableScrolling}
                          onContactUser={onContactUser}
                          monthlyTimeSlots={monthlyTimeSlots}
                          onFetchTimeSlots={onFetchTimeSlots}
                          onFetchTransactionLineItems={
                            onFetchTransactionLineItems
                          }
                          lineItems={lineItems}
                          fetchLineItemsInProgress={fetchLineItemsInProgress}
                          fetchLineItemsError={fetchLineItemsError}
                          validListingTypes={config.listing.listingTypes}
                          marketplaceCurrency={config.currency}
                          dayCountAvailableForBooking={
                            config.stripe.dayCountAvailableForBooking
                          }
                          marketplaceName={config.marketplaceName}
                          showCartButton={true}
                          currentUser={currentUser}
                          isCurrentUserListing={
                            currentAuthor?.id?.uuid === currentUser?.id?.uuid
                          }
                          onAddToCart={addToCartHandler}
                          addToCartInProgress={addToCartInProgress}
                          addToCartError={addToCartError}
                          cartNotification={cartNotification}
                          routeConfiguration={routeConfiguration}
                          isVerified={isVerified}
                          reviews={reviews}
                          rating={rating}
                          totalListings={totalListings}
                          isMobile={isMobile}
                        />
                        {/* About Owner */}
                        <div className={css.aboutOwnerMobile}>
                          <AboutOwner
                            profile={ensuredAuthor}
                            listingsCount={totalListings}
                            reviews={reviews}
                            rating={rating}
                            verified={isVerified}
                            intl={intl}
                            onClick={() => !isOwnListing ? onContactUser(ensuredAuthor) : null}
                            storeLink={
                              <NamedLink
                                className={css.visitStore}
                                name="ProfilePage"
                                params={{ id: ensuredAuthor?.id?.uuid }}
                              >
                                <FormattedMessage id="AboutOwner.visitStore" />
                                <i className="ri-arrow-right-line"></i>
                              </NamedLink>
                            }

                          />
                        </div>
                        <div className={css.BookingSupport}>
                          <BookingSupport variant="lg" />
                        </div>
                      </div>
                    </Column>
                  ) : null}
                  <DescriptionRentalTermsTabs
                    description={sanitizeHtml(description)}
                    rentalTerms={{
                      cashSecurityDeposit: publicData?.security_deposit || '',
                      cancelationPolicy: publicData?.cancelation_policy || '',
                      rentalAccessibility: publicData?.access_to_rentals || [],
                      trustVerification: publicData?.Trust_and_verification || [],
                      reasonForRenting: publicData?.reason_for_listing || [],
                    }}
                    faqs={showFaqs ? faqs : []}
                  />

                  {/* Instructions */}
                  {showInstructions && <InstructionsToUse content={instructions} />}

                  {/* Map */}
                  <div id='location'>
                    <SectionMapMaybe
                      geolocation={geolocation}
                      publicData={publicData}
                      listingId={currentListing.id}
                      mapsConfig={config.maps}
                      locationText={locationText}
                      title={title}
                    />
                  </div>

                  {/* Suggested Uses */}
                  {showSuggestedUsage && (
                    <SuggestedUses uses={suggested_usage_of_this_item || []} />
                  )}

                  {/* Where Works Best */}
                  {showWhereTheItemCanBeUsed && (
                    <WhereWorksBest content={sanitizeHtml(where_the_item_can_be_used)} />
                  )}

                  {/* Environmental Impact */}
                  {showEnvironmentalImpactSection && environmentalImpactData && (
                    <EnvironmentalImpact data={{
                      title: "Environmental Impact",
                      subtitle: "Renting isn’t just profitable — it’s sustainable.",
                      ...environmentalImpactData
                    }} />
                  )}

                  {/* About Owner */}
                  <div className={css.aboutOwnerDesktop}>
                    <AboutOwner
                      profile={ensuredAuthor}
                      listingsCount={totalListings}
                      reviews={reviews}
                      rating={rating}
                      verified={isVerified}
                      intl={intl}
                      onClick={() => !isOwnListing ? onContactUser(ensuredAuthor) : null}
                      storeLink={
                        <NamedLink
                          className={css.visitStore}
                          name="ProfilePage"
                          params={{ id: ensuredAuthor?.id?.uuid }}
                        >
                          <FormattedMessage id="AboutOwner.visitStore" />
                          <i className="ri-arrow-right-line"></i>
                        </NamedLink>
                      }

                    />
                  </div>

                  <div id='reviews'>
                    <StoreReviews
                      reviews={reviews}
                      averageRating={rating}
                      totalReviews={reviews ? reviews.length : 0}
                    />
                  </div>


                  {showOnlineReviewSummary && (
                    <ReviewSummary
                      listingTitle={title}
                      reviewSummary={{
                        content: online_review_summary?.content,
                        rating: online_review_summary?.rating
                      }}
                    />
                  )}


                </Column>
                <Column lg={4} md={4} sm={12} className={css.desktopColumn}>
                  <div className={css.orderColumnForProductLayout}>
                    <OrderPanel
                      className={css.productOrderPanel}
                      listing={currentListing}
                      isOwnListing={isOwnListing}
                      onSubmit={handleOrderSubmit}
                      showAuthorDetails
                      breakdownTitle={<FormattedMessage id="BookingDatesForm.priceBreakdownTitle" />}
                      authorLink={
                        <NamedLink
                          className={css.authorNameLink}
                          name="ListingPage"
                          params={params}
                          to={{ hash: '#author' }}
                        >
                          {storeName || authorDisplayName}
                        </NamedLink>
                      }
                      title={
                        <FormattedMessage
                          id="ListingPage.orderTitle"
                          values={{ title: richTitle }}
                        />
                      }
                      titleDesktop={
                        <H4 as="h1" className={css.orderPanelTitle}>
                          <FormattedMessage
                            id="ListingPage.orderTitle"
                            values={{ title: richTitle }}
                          />
                        </H4>
                      }
                      payoutDetailsWarning={payoutDetailsWarning}
                      author={ensuredAuthor}
                      onManageDisableScrolling={onManageDisableScrolling}
                      onContactUser={onContactUser}
                      monthlyTimeSlots={monthlyTimeSlots}
                      onFetchTimeSlots={onFetchTimeSlots}
                      onFetchTransactionLineItems={onFetchTransactionLineItems}
                      lineItems={lineItems}
                      fetchLineItemsInProgress={fetchLineItemsInProgress}
                      fetchLineItemsError={fetchLineItemsError}
                      validListingTypes={config.listing.listingTypes}
                      marketplaceCurrency={config.currency}
                      dayCountAvailableForBooking={
                        config.stripe.dayCountAvailableForBooking
                      }
                      marketplaceName={config.marketplaceName}
                      showCartButton={true}
                      currentUser={currentUser}
                      isCurrentUserListing={
                        currentAuthor?.id?.uuid === currentUser?.id?.uuid
                      }
                      onAddToCart={addToCartHandler}
                      addToCartInProgress={addToCartInProgress}
                      addToCartError={addToCartError}
                      cartNotification={cartNotification}
                      routeConfiguration={routeConfiguration}
                      isVerified={isVerified}
                      reviews={reviews}
                      totalListings={totalListings}
                    />
                    <div className={css.BookingSupport}>
                      <BookingSupport variant="lg" />
                    </div>
                  </div>
                </Column>
              </Grid>
            </Column>
          </Grid>

          <AuthorListings
            intl={intl}
            authorListings={authorListings}
            authorListingsInProgress={authorListingsInProgress}
            authorListingsError={authorListingsError}
            author={ensuredAuthor}
            isVerified={isVerified}
            currentUserCurrency={selectedCurrency}
            exchangeRates={exchangeRates}
            history={history}
            routes={routeConfiguration}
            queryID={queryID}

          />
          <div className={css.similarItemSection}>
            <BlogListing blogs={blogs || []} fetchBlogsInProgress={fetchBlogsInProgress} fetchBlogsError={fetchBlogsError} />
          </div>
        </Container>
      </LayoutSingleColumn>
    </Page>
  );
};

ListingPageComponent.defaultProps = {
  currentUser: null,
  inquiryModalOpenForListingId: null,
  showListingError: null,
  reviews: [],
  fetchReviewsError: null,
  monthlyTimeSlots: null,
  sendInquiryError: null,
  lineItems: null,
  fetchLineItemsError: null,
};

ListingPageComponent.propTypes = {
  // from useHistory
  history: shape({
    push: func.isRequired,
  }).isRequired,
  // from useLocation
  location: shape({
    search: string,
  }).isRequired,

  // from useIntl
  intl: intlShape.isRequired,

  // from useConfiguration
  config: object.isRequired,
  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,

  params: shape({
    id: string.isRequired,
    slug: string,
    variant: oneOf([
      LISTING_PAGE_DRAFT_VARIANT,
      LISTING_PAGE_PENDING_APPROVAL_VARIANT,
    ]),
  }).isRequired,

  isAuthenticated: bool.isRequired,
  currentUser: propTypes.currentUser,
  getListing: func.isRequired,
  getOwnListing: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  scrollingDisabled: bool.isRequired,
  inquiryModalOpenForListingId: string,
  showListingError: propTypes.error,
  callSetInitialValues: func.isRequired,
  reviews: arrayOf(propTypes.review),
  fetchReviewsError: propTypes.error,
  monthlyTimeSlots: object,
  // monthlyTimeSlots could be something like:
  // monthlyTimeSlots: {
  //   '2019-11': {
  //     timeSlots: [],
  //     fetchTimeSlotsInProgress: false,
  //     fetchTimeSlotsError: null,
  //   }
  // }
  sendInquiryInProgress: bool.isRequired,
  sendInquiryError: propTypes.error,
  onSendInquiry: func.isRequired,
  onInitializeCardPaymentData: func.isRequired,
  onFetchTransactionLineItems: func.isRequired,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,
  viewport: shape({
    width: number.isRequired,
    height: number.isRequired,
  }),
};

const EnhancedListingPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  return (
    <ListingPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  const {
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    sendInquiryInProgress,
    sendInquiryError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    inquiryModalOpenForListingId,
    authorListingsInProgress,
    authorListingsError,
    authorListingsIds = [],
    replicateListingInProgress,
    replicateListingError,
    isVerified,
    totalListings,
    blogs,
    fetchBlogsInProgress,
    fetchBlogsError,
  } = state.ListingPage;

  const { currentUser, currentUserCurrency, currentUserLocationInfo } = state.user;
  const {
    exchangeRates,
    fetchRatesError,
    fetchRatesInProgress,
  } = state.exchangeRate;
  const { addToCartInProgress, addToCartError, notification } = state.cart;

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const authorListingsRefs = authorListingsIds.map(id => ({
    id,
    type: 'listing',
  }));
  const authorListings = getMarketplaceEntities(state, authorListingsRefs);

  return {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    inquiryModalOpenForListingId,
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    sendInquiryInProgress,
    sendInquiryError,
    selectedCurrency: currentUserCurrency,
    exchangeRates,
    fetchRatesError,
    fetchRatesInProgress,
    authorListings,
    authorListingsInProgress,
    authorListingsError,
    addToCartInProgress,
    addToCartError,
    cartNotification: notification,
    replicateListingError,
    replicateListingInProgress,
    isVerified,
    totalListings,
    blogs,
    fetchBlogsInProgress,
    fetchBlogsError,
    currentUserLocationInfo,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onFetchTransactionLineItems: params =>
    dispatch(fetchTransactionLineItems(params)),
  onSendInquiry: (listing, message) => dispatch(sendInquiry(listing, message)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onFetchTimeSlots: (listingId, start, end, timeZone) =>
    dispatch(fetchTimeSlots(listingId, start, end, timeZone)),
  onAddToCart: (config, params, queryID) =>
    dispatch(addToCart(config, params, queryID)),
  onReplicateListing: listingId => dispatch(replicateListingThunk(listingId)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const ListingPage = compose(
  withViewport,
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )
)(EnhancedListingPage);

export default ListingPage;
